import React, { useEffect, useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import ModalComponent from "../../../components/Modal";
import FormField from "../../../components/FormField";
import FormButton from "../../../components/FormButton";
import FormRow from "../../../components/FormRow";
import CustomDropdown from "../../../components/CustomDropdown";
import MuiDatePicker from "../../../components/MuiDatePicker";
import {
  requestRequestTimeOffAction,
  resetRequestTimeOffStateAction,
  requestUpdateTimeOffAction,
  resetUpdateTimeOffStateAction,
  resetGetTimeOffRequestsStateAction,
  requestGetTimeOffRequestsAction,
  requestGetAllTimeOffRequestsUsingUserIdAction,
} from "../../../Redux/TimeOffState/TimeOffActionCreator";
import { CalendarTodayRounded } from "@mui/icons-material";
import * as Yup from "yup";
import TextAreaInput from "../../../components/TextArea";

const TimeOffForm = ({ title, timeOffData, onClose }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestTimeOffState = useSelector(
    (state) => state.RequestTimeOffRequestReducer
  );
  const updateTimeOffState = useSelector(
    (state) => state.UpdateTimeOffReducer
  );

  const timeOffTypes = [
    { value: "CASUAL_LEAVE", label: "Casual Leave (CL)" },
    { value: "SICK_LEAVE", label: "Sick Leave (SL)" },
    { value: "PAID_LEAVE", label: "Paid Leave (PL)" },
    { value: "UNPAID_LEAVE", label: "Unpaid Leave (LOP)" },
    { value: "MATERNITY_LEAVE", label: "Maternity Leave" },
    { value: "PUBLIC_HOLIDAY", label: "Public Holiday" },
  ];

  const validationSchema = Yup.object({
    startDate: Yup.date()
      .required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date must be after start date"),
    type: Yup.string().required("Time off type is required"),
    reason: Yup.string()
      .required("Reason is required")
      .min(10, "Reason must be at least 10 characters")
      .max(500, "Reason cannot exceed 500 characters"),
  });

  const initialValues = {
    startDate: timeOffData?.startDate || "",
    endDate: timeOffData?.endDate || "",
    type: timeOffData?.type || "",
    reason: timeOffData?.reason || "",
    submittedDate: timeOffData?.submittedDate || "",
  };

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setIsSubmitting(true);

      const payload = {
        startDate: moment(values.startDate).format("MM-DD-YYYY"),
        endDate: moment(values.endDate).format("MM-DD-YYYY"),
        type: values.type,
        reason: values.reason.trim(),
      };

      // Check if this is an update (timeOffData has an id) or create
      if (timeOffData?.id) {
        // Update existing time off request
        dispatch(requestUpdateTimeOffAction({ id: timeOffData.id, data: payload }));
      } else {
        // Create new time off request
        dispatch(requestRequestTimeOffAction(payload));
      }
    } catch (error) {
      setIsSubmitting(false);
      toast.error("Error submitting time off request");
    }
  };

  const handleClose = () => {
    // dispatch(closeTimeOffModal()); // Action not defined in TimeOffActionCreator
    onClose();
  };

  useEffect(() => {
    // Handle create response
    if (
      requestTimeOffState?.response &&
      !requestTimeOffState?.loading &&
      !requestTimeOffState?.error
    ) {
      setIsSubmitting(false);
      dispatch(resetRequestTimeOffStateAction());
      // toast.success("Time off request submitted successfully");
      handleClose();
    }

    if (requestTimeOffState?.error && !requestTimeOffState?.loading) {
      setIsSubmitting(false);
      // toast.error("Failed to submit time off request");
      dispatch(resetRequestTimeOffStateAction());
    }

    // Handle update response
    if (
      updateTimeOffState?.response &&
      !updateTimeOffState?.loading &&
      !updateTimeOffState?.error
    ) {
      setIsSubmitting(false);
      dispatch(resetUpdateTimeOffStateAction());
      toast.success("Time off request updated successfully");

      handleClose();
      dispatch(resetGetTimeOffRequestsStateAction());
      dispatch(requestGetTimeOffRequestsAction({ page: 0, size: 10, sort: 'desc' }));
      dispatch(requestGetAllTimeOffRequestsUsingUserIdAction());
    }

    if (updateTimeOffState?.error && !updateTimeOffState?.loading) {
      setIsSubmitting(false);
      toast.error("Failed to update time off request");
      dispatch(resetUpdateTimeOffStateAction());
    }
  }, [requestTimeOffState, updateTimeOffState, dispatch]);

  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = moment(startDate);
    const end = moment(endDate);
    return end.diff(start, "days") + 1;
  };

  return (
    <ModalComponent
      show={true}
      onClose={handleClose}
      title={
        <span className="text-indigo-500 flex items-center">
          <CalendarTodayRounded className="h-5 w-5 mr-2" />
          {title}
        </span>
      }
      className="w-full max-w-2xl mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={true}
        validateOnSubmit={true}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => {
          const daysCount = calculateDays(values.startDate, values.endDate);

          return (
            <Form className="p-2">
              <div className="space-y-6">
                {/* Time Off Type */}
                <div className="relative">
                  <CustomDropdown
                    value={timeOffTypes.find(
                      (type) => type.value === values.type
                    )}
                    name="type"
                    customClasses="w-full text-left text-sm"
                    handleChange={(selectedOption) => {
                      setFieldValue("type", selectedOption?.value);
                    }}
                    placeholder="Select time off type"
                    options={timeOffTypes}
                    handleGetOptionValue={(option) => option.value}
                    handleGetOptionName={(option) => option.label}
                    required
                    isDisabled={title === "View Time Off Request"}
                  />
                  {errors.type && touched.type && (
                    <div className="text-red-500 text-sm mt-1 text-left">
                      {errors.type}
                    </div>
                  )}
                </div>

                {/* Date Range */}
                <FormRow className="flex-col sm:flex-row gap-4">
                  <div className="w-full relative">
                    <MuiDatePicker
                      label="Start Date"
                      value={values.startDate ? moment(values.startDate) : null}
                      onChange={(date) => {
                        if (date) {
                          setFieldValue("startDate", date.format("YYYY-MM-DD"));

                          // If end date is before start date, update it
                          if (values.endDate && moment(values.endDate).isBefore(date)) {
                            setFieldValue("endDate", date.format("YYYY-MM-DD"));
                          }
                        }
                      }}
                      className="text-sm"
                      required
                      disabled={title === "View Time Off Request"}
                    />
                    {errors.startDate && touched.startDate && (
                      <p className="mt-1 text-sm text-rose-500 text-left">
                        {errors.startDate}
                      </p>
                    )}
                  </div>

                  <div className="w-full relative">
                    <MuiDatePicker
                      label="End Date"
                      value={values.endDate ? moment(values.endDate) : null}
                      onChange={(date) => {
                        if (date) {
                          setFieldValue("endDate", date.format("YYYY-MM-DD"));
                        }
                      }}
                      minDate={
                        values.startDate
                          ? moment(values.startDate)
                          : moment()
                      }
                      className="text-sm"
                      required
                      disabled={title === "View Time Off Request"}
                    />
                    {errors.endDate && touched.endDate && (
                      <p className="mt-1 text-sm text-rose-500 text-left">
                        {errors.endDate}
                      </p>
                    )}
                  </div>
                </FormRow>
                {title === "View Time Off Request" && title !== "Request Time Off" && (
                  <div className="w-1/2 relative">
                    <MuiDatePicker
                      label="Submitted Date"
                      value={values.submittedDate ? moment(values.submittedDate) : null}
                      onChange={(date) => {
                        if (date) {
                          setFieldValue("submittedDate", date.format("YYYY-MM-DD"));
                        }
                      }}
                      className="text-sm"
                      disabled={true}
                    />
                    {errors.submittedDate && touched.submittedDate && (
                      <div className="text-red-500 text-sm mt-1 text-left">
                        {errors.submittedDate}
                      </div>
                    )}
                  </div>
                )}

                {/* Days Count */}
                {daysCount > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-900">
                        Total Days:
                      </span>
                      <span className="text-lg font-semibold text-blue-600">
                        {daysCount} {daysCount === 1 ? "day" : "days"}
                      </span>
                    </div>
                  </div>
                )}

                {/* Reason */}
                <div className="relative">
                  <TextAreaInput
                    value={values.reason}
                    name="reason"
                    placeholder="Please provide a detailed reason for your time off request"
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    classes="w-full"
                    legend={true}
                    rows={4}
                    isDisabled={title === "View Time Off Request"}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.reason && touched.reason && (
                      <div className="text-red-500 text-sm">
                        {errors.reason}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 ml-auto">
                      {values.reason?.length || 0}/500 characters
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                {title !== "View Time Off Request" && (
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                    <FormButton
                      type="button"
                      onClick={handleClose}
                      className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                    >
                      Cancel
                    </FormButton>

                    <FormButton
                      type="submit"
                      disabled={
                        isSubmitting ||
                        requestTimeOffState?.loading ||
                        updateTimeOffState?.loading ||
                        title === "View Time Off Request"
                      }
                      className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting || requestTimeOffState?.loading || updateTimeOffState?.loading
                        ? (timeOffData?.id ? "Updating..." : "Submitting...")
                        : title === "View Time Off Request"
                          ? "Submit"
                          : timeOffData?.id
                            ? "Update"
                            : "Submit"}
                    </FormButton>
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </ModalComponent>
  );
};

export default TimeOffForm;


